import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell, YAxis, CartesianGrid } from "recharts";
import { Card } from "react-bootstrap";
import styles from "./RevenueChart.module.css";
import cn from "classnames";
import Calender, { CalenderBtnTheme } from "../../common/Calender/Calender";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchRevenueChartData } from "../../../redux/slices/dashboard/actions";
import { startOfYear, endOfYear } from "date-fns";

interface CustomTickProps {
  x: number;
  y: number;
  payload: {
    value: string;
  };
}

const CustomTooltip = ({ active, payload, label }: {
    active?: boolean;
    payload?: any;
    label?: string;
  }) => {
  if (active && payload && payload.length) {
    return (
      <div className={cn(styles.customTooltip)}>
        <div className={styles.tooltipLabel}>{label}</div>
        <div className={cn(styles.tooltipLabel)}>£{payload[0].value.toLocaleString()}</div>
      </div>
    );
  }
  return null;
};

const CustomTick: React.FC<CustomTickProps> = ({ x, y, payload }) => {
  // const isCurrent = payload.value === currentMonth;
  return (
    <text
      x={x - 10}
      y={y + 14}
      // fill={isCurrent ? "var(--lightGrayishNavy2)" : "var(--lightGrayishNavy)"}
      fill="var(--lightGrayishNavy)"
      fontSize={12}
      fontWeight={"500" as const}
      style={{ fontFamily: "var(--basefont)" }}
    >
      {payload.value}
    </text>
  );
};

const RevenueChart: React.FC<{ refresh: number }> = ({ refresh }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { revenueChartData, revenueChartLoading } = useSelector((state: RootState) => state.dashboard);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([startOfYear(new Date()), endOfYear(new Date())]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [startDate, endDate] = dateRange;

   useEffect(() => {
      if (startDate && endDate) {
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const day = date.getDate().toString().padStart(2, "0");
          return `${year}-${month}-${day}`;
        };
  
        dispatch(
          fetchRevenueChartData({
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
          })
        );
      }
    }, [startDate, endDate, dispatch, refresh]);

  const handleDateChange = (range: [Date | null, Date | null], filter: string | null) => {
    setDateRange(range);
    setActiveFilter(filter);
  };

  const dataForChart = revenueChartData?.data;

  const getInterval = (dataLength: number | undefined) => {
    if (!dataLength) return 0;
    if (dataLength > 15) {
        return Math.floor(dataLength / 15);
    }
    return 0;
  }

  // const maxValue = dataForChart ? Math.max(...dataForChart.map(m => m.value)) : 0;
  // const step = Math.ceil(maxValue / 5);
  // const roundedMax = step > 0 ? step * 5 : 5;
  // const ticks = Array.from({ length: 6 }, (_, i) => i * step);

  return (
    <Card bsPrefix={styles.revenueChartCard}>
      <Card.Header bsPrefix={styles.revenueChartCardHeader}>
        <div className={styles.cardHeaderLeft}>
          <div className={styles.cardHeaderTitles}>
            <Card.Title as={'div'} bsPrefix={styles.title}>Paid Plan Revenue</Card.Title>
          </div>
          <div className={styles.downloadsWrap}>
            <div className={styles.downloadsCard}>
              <Card.Title as={'div'} className={styles.downloadsCardTitle}>
                £{revenueChartLoading === 'succeeded' && revenueChartData ? revenueChartData.overallRevenue.toLocaleString() : '...'}
              </Card.Title>
              <Card.Text as={'div'} className={styles.downloadsCardText}>Total Revenue</Card.Text>
            </div>
          </div>
        </div>
        <div className={styles.cardHeaderRight}>
          <Calender calenderBtnTheme={CalenderBtnTheme.LIGHT} onDateChange={handleDateChange} dateRange={dateRange} activeFilter={activeFilter}/>
        </div>
      </Card.Header>

      <Card.Body bsPrefix={styles.revenueChartCardBody}>
        <div style={{width: '100%', height: 297}}>
        {revenueChartLoading === 'pending' && <p>Loading...</p>}
        {revenueChartLoading === 'succeeded' && dataForChart && (
          <ResponsiveContainer>
            <BarChart data={dataForChart} margin={{ top: 0, right: 35, bottom: 35, left: 25 }}>
              <CartesianGrid 
                vertical={false} 
                horizontal={true} 
                stroke="var(--paleNavy)"
                strokeWidth={1} 
                strokeDasharray="2 4"
              />

              <XAxis
                dataKey="name"
                interval={getInterval(dataForChart?.length)}
                axisLine={true}
                tickLine={true}
                ticks={dataForChart.map(m => m.name)}
                tick={<CustomTick x={0} y={0} payload={{
                  value: ""
                }}
                />}
                label={{
                  value:
                    revenueChartData?.period === "daily"
                        ? "Time ->"
                        : revenueChartData?.period === "weekly"
                        ? "Dates ->"
                        : revenueChartData?.period === "monthly"
                        ? "Weeks ->"
                        : "Months ->",
                  position: "insideBottom",
                  offset: -30,
                  fill: "var(--black2)",
                  fontSize: 14,
                  fontWeight: 700,
                }}
              />

              <YAxis 
                axisLine={true}
                tickLine={true}
                tick={{ 
                  fill: "var(--lightGrayishNavy2)",
                  fontSize:12,
                  fontWeight: 500,
                  fontFamily: "var(--basefont)"
                }}
                label={{
                  value: "Revenue ->",
                  angle: -90,
                  position: "insideLeft",
                  fill: "var(--black2)",
                  fontSize: 14,
                  fontWeight: 700,
                }}
                tickMargin={5}
                padding={{ top: 0, bottom: 0 }}
                // domain={[0, roundedMax]}
                // ticks={ticks}
              />

              <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />
              <Bar dataKey="value" radius={[40, 40, 0, 0]} barSize={14}>
                {dataForChart.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    // fill={entry.name === currentMonth ? "var(--lightOrange2)" : "var(--brightGreen)"}
                    fill={"var(--brightGreen)"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        {revenueChartLoading === 'failed' && <p>Error loading data.</p>}
        </div>
      </Card.Body>
    </Card>
  );
};

export default React.memo(RevenueChart);